'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Box, Button, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import GMDrawerModal from '@/components/shared/GMDrawerModal'
import { SsoProviderIcon } from '@/components/Icons/SsoProviderIcon'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { clearPendingSsoLink, getPendingSsoLinkProvider } from '@/lib/auth/clearPendingSsoLink'
import { isSsoLinkPromptSurface } from '@/lib/auth/pendingSsoLink'
import { SSO_PROVIDERS, type SsoProviderKey } from '@/lib/auth/ssoProviders'
import { useGetSsoStatusQuery, useStartSsoOAuthMutation } from '@/lib/store/api/userApi'

/**
 * Offers to link a provider after someone signs in with a phone number following a rejected
 * "Sign in with <provider>" attempt. Mount only when the `pending_sso_link` cookie names this
 * provider and its SSO flag is enabled; the cookie is cleared once the person links or declines
 * (the status query clears it when the provider is already linked).
 */
export function SsoLinkPrompt({ provider }: { provider: SsoProviderKey }) {
    const { label } = SSO_PROVIDERS[provider]
    const pathname = usePathname()
    const [isMounted, setIsMounted] = useState(false)
    const [isDismissed, setIsDismissed] = useState(false)
    const [isRedirecting, setIsRedirecting] = useState(false)
    const [startSsoOAuth, { isLoading: isStarting }] = useStartSsoOAuthMutation()

    useEffect(() => {
        setIsMounted(true)
    }, [])

    const isPending =
        isMounted &&
        !isDismissed &&
        getPendingSsoLinkProvider() === provider &&
        isAuthenticatedViaMiddlewareCookie() &&
        isSsoLinkPromptSurface(pathname)

    const { data: status } = useGetSsoStatusQuery(provider, { skip: !isPending })
    const alreadyLinked = Boolean(status?.linked)

    const dismiss = useCallback(() => {
        clearPendingSsoLink()
        setIsDismissed(true)
    }, [])

    const linkProvider = useCallback(async () => {
        setIsRedirecting(true)
        try {
            const { authorize_url: authorizeUrl } = await startSsoOAuth(provider).unwrap()
            if (!authorizeUrl) {
                throw new Error('sso_link_prepare_missing_url')
            }
            clearPendingSsoLink()
            window.location.assign(authorizeUrl)
        } catch {
            setIsRedirecting(false)
            notifications.show({
                color: 'red',
                title: `Could not start ${label} linking`,
                message: 'Please try again in a moment.',
            })
        }
    }, [startSsoOAuth, provider, label])

    const opened = isPending && status !== undefined && !alreadyLinked

    return (
        <GMDrawerModal
            opened={opened}
            onClose={dismiss}
            size="sm"
            drawerSize={380}
            drawerRadius="var(--gm-radius-24)"
            title={
                <Stack component="span" gap="md" data-dd-privacy="allow">
                    <Box
                        component="span"
                        w={48}
                        h={48}
                        bg="gray.1"
                        aria-hidden="true"
                        style={{ borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <SsoProviderIcon provider={provider} size={24} />
                    </Box>
                    <Text component="span" fz="xl" fw="var(--mantine-h2-font-weight)" c="gray.9">
                        Turn on {label} sign in?
                    </Text>
                </Stack>
            }
        >
            <Stack gap="md" pb="sm" data-testid="sso-link-prompt">
                <Stack gap={4}>
                    <Text c="gray.7" data-dd-privacy="allow">
                        You tried signing in with {label}. Would you like to link your {label} account?
                    </Text>
                </Stack>
                <Stack gap="sm" mt="xs">
                    <Button
                        radius="xl"
                        size="lg"
                        fullWidth
                        loading={isStarting || isRedirecting}
                        onClick={() => void linkProvider()}
                        data-testid="sso-link-prompt-link"
                    >
                        Link {label} account
                    </Button>
                    <Button
                        variant="outline"
                        color="gray"
                        radius="xl"
                        size="lg"
                        fullWidth
                        disabled={isStarting || isRedirecting}
                        onClick={dismiss}
                        data-testid="sso-link-prompt-dismiss"
                    >
                        Not now
                    </Button>
                </Stack>
            </Stack>
        </GMDrawerModal>
    )
}
